/**
 * Program oluşturma.
 *
 * Ekranın kurgusu bilinçli: sistem bir ÖNERİ getiriyor, uygulayıcı
 * onaylıyor ya da değiştiriyor. Tersi de mümkündü — boş bir form verip
 * ağırlıkları elle doldurtmak — ama o zaman ölçüm yapmanın programa
 * hiçbir katkısı olmazdı ve her uygulayıcı kendi sezgisiyle farklı
 * program açardı.
 *
 * Önerinin GEREKÇESİ de gösteriliyor. "Sistem böyle dedi" diye bir şey
 * yok: hangi kuralın neden tetiklendiği yazılı, uygulayıcı katılmazsa
 * ağırlığı değiştirebiliyor ve değiştirdiği kayda geçiyor.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ALANLAR,
  ALAN_ADI,
  ApiHatasi,
  api,
  type Alan,
  type ProgramOnerisi,
  type ProgramOnizleme,
} from '../api'

function bugunArti(gun: number): string {
  const d = new Date()
  d.setDate(d.getDate() + gun)
  return d.toISOString().slice(0, 10)
}

export function ProgramOlustur({
  danisanId,
  onBitti,
  onIptal,
}: {
  danisanId: string
  onBitti: () => void
  onIptal: () => void
}) {
  const [oneri, setOneri] = useState<ProgramOnerisi | null>(null)
  const [odak, setOdak] = useState<Partial<Record<Alan, number>>>({})
  const [hafta, setHafta] = useState(8)
  const [dakika, setDakika] = useState(25)
  const [kanal, setKanal] = useState<'digital' | 'print' | 'both'>('both')
  const [baslangic, setBaslangic] = useState(bugunArti(1))
  const [seviye, setSeviye] = useState(4)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)
  const [onizleme, setOnizleme] = useState<ProgramOnizleme | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await api.programOnerisi(danisanId)
      setOneri(r)
      setOdak(r.odak)
      setSeviye(r.seviye)
    } catch {
      setHata('Öneri alınamadı.')
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  /**
   * Ayarlar değiştikçe "bu ayarlarla haftada kaç egzersiz çıkacak"
   * satırını tazeler.
   *
   * Uygulayıcı ekranda DAKİKA giriyor; karşılığında yirmi sayfalık
   * bir kâğıt seti çıkacağını program oluştuktan sonra öğrenmesi
   * geç oluyordu.
   *
   * Gecikme var: her tuş vuruşunda sunucuya gitmek gereksiz.
   */
  useEffect(() => {
    if (!oneri) return
    let iptal = false
    const zaman = setTimeout(() => {
      api
        .programOnizleme({
          danisanId,
          haftaSayisi: hafta,
          haftalikDakika: dakika,
          kanal,
          seviye,
          odak,
          ...(oneri.temelOlcumId ? { temelOlcumId: oneri.temelOlcumId } : {}),
        })
        .then((r) => {
          if (!iptal) setOnizleme(r)
        })
        .catch(() => {
          if (!iptal) setOnizleme(null)
        })
    }, 400)
    return () => {
      iptal = true
      clearTimeout(zaman)
    }
  }, [danisanId, hafta, dakika, kanal, seviye, odak, oneri])

  if (hata && !oneri) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onIptal}>← Geri</button>
      </>
    )
  }
  if (!oneri) return <p className="alt">Öneri hazırlanıyor…</p>

  const degistirildi = JSON.stringify(odak) !== JSON.stringify(oneri.odak)

  const olustur = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      await api.programOlustur({
        danisanId,
        haftaSayisi: hafta,
        haftalikDakika: dakika,
        kanal,
        baslangic,
        seviye,
        odak,
        ...(oneri.temelOlcumId ? { temelOlcumId: oneri.temelOlcumId } : {}),
      })
      onBitti()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'onam_yok'
          ? 'Program onamı alınmadan program açılamıyor. Dosyadan onamı kaydedin.'
          : 'Program oluşturulamadı.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onIptal}>← Dosya</button>
      </div>

      <h1>Program oluştur</h1>
      <p className="alt">
        Sistem son ölçümden bir odak öneriyor. Katılmıyorsanız ağırlıkları değiştirin;
        değiştirdiğiniz kayda geçer.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {!oneri.olcumVar && (
        <div className="bilgi">
          Bu dosyada tamamlanmış ölçüm yok, bu yüzden odak önerisi boş geldi. Program yine
          açılabilir ama ağırlıkları siz vereceksiniz.
        </div>
      )}

      {oneri.olcumVar && oneri.notrOlcum && (
        <div className="bilgi">
          Ölçüm dikkat çeken bir örüntü göstermedi. Ağırlık verilmezse program alanlara dengeli
          dağılır — bu da geçerli bir tercih.
        </div>
      )}

      {oneri.gerekce.length > 0 && (
        <div className="kart">
          <h2 style={{ margin: '0 0 4px' }}>Öneri neye dayanıyor</h2>
          <p className="alt" style={{ marginBottom: 10 }}>
            Aşağıdakiler yaşıt karşılaştırması değil; ölçümün kendi içinde yorumlanabilen
            örüntüler.
          </p>
          {oneri.gerekce.map((g, i) => (
            <div
              key={i}
              style={{ padding: '8px 0', borderTop: '1px solid var(--line)', fontSize: 14 }}
            >
              <strong>{ALAN_ADI[g.domain] ?? g.domain}</strong> — {g.aciklama}
              <div className="alt" style={{ margin: '2px 0 0', fontSize: 12 }}>
                kural: {g.kural}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="kart">
        <h2 style={{ margin: '0 0 4px' }}>Odak ağırlıkları</h2>
        <p className="alt" style={{ marginBottom: 10 }}>
          Ağırlık verilmeyen alan tamamen dışlanmaz; tek alana kilitlenen program çocuğu sıkar.
        </p>
        {ALANLAR.map((a) => (
          <div
            key={a}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 0',
              borderTop: '1px solid var(--line)',
            }}
          >
            <span style={{ flex: 1 }}>{ALAN_ADI[a]}</span>
            {oneri.odak[a] !== undefined && (
              <span className="alt" style={{ margin: 0, fontSize: 12 }}>
                öneri {oneri.odak[a]}
              </span>
            )}
            <input
              type="number"
              min={0}
              max={5}
              step={0.1}
              value={odak[a] ?? 0}
              onChange={(e) =>
                setOdak({ ...odak, [a]: Math.max(0, Math.min(5, Number(e.target.value))) })
              }
              style={{ width: 90 }}
            />
          </div>
        ))}
        {degistirildi && (
          <div className="bilgi" style={{ marginTop: 12, marginBottom: 0 }}>
            Öneriyi değiştirdiniz. Bu, programın kaydına işlenecek.
          </div>
        )}
      </div>

      <div className="kart">
        <h2 style={{ margin: '0 0 10px' }}>Program ayarları</h2>
        <div className="satir">
          <label>
            Kaç hafta
            <input
              type="number"
              min={1}
              max={24}
              value={hafta}
              onChange={(e) => setHafta(Number(e.target.value))}
            />
          </label>
          <label>
            Haftalık süre (dakika)
            <input
              type="number"
              min={5}
              max={120}
              step={5}
              value={dakika}
              onChange={(e) => setDakika(Number(e.target.value))}
            />
          </label>
          <label>
            Başlangıç
            <input
              type="date"
              value={baslangic}
              onChange={(e) => setBaslangic(e.target.value)}
            />
          </label>
          <label>
            Başlangıç zorluğu
            <input
              type="number"
              min={1}
              max={10}
              value={seviye}
              onChange={(e) => setSeviye(Number(e.target.value))}
            />
          </label>
          <label>
            Teslim biçimi
            <select value={kanal} onChange={(e) => setKanal(e.target.value as never)}>
              <option value="both">Dönüşümlü — bir hafta tablet, bir hafta kâğıt</option>
              <option value="digital">Yalnızca tablet</option>
              <option value="print">Yalnızca kâğıt</option>
            </select>
          </label>
        </div>
        <p className="alt" style={{ marginBottom: 10 }}>
          Önerilen başlangıç zorluğu {oneri.seviye}. Temkinli seçilmiştir: fazla kolay başlamak
          toparlanabilir, fazla zor başlamak çocuğu ilk haftada kaybettirir.
        </p>

        {onizleme && (
          <div className="bilgi">
            <strong>Bu ayarlarla ne çıkıyor</strong>
            {onizleme.haftalar.map((h) => (
              <div key={h.kanal} style={{ marginTop: 4 }}>
                {onizleme.haftalar.length > 1
                  ? `${h.kanal === 'digital' ? 'Tek haftalar (tablet)' : 'Çift haftalar (kâğıt)'}: `
                  : ''}
                haftada{' '}
                <strong>
                  {h.enAz === h.enCok ? h.enAz : `${h.enAz}–${h.enCok}`} egzersiz
                </strong>
                , yaklaşık {h.dakika} dakika.
                {h.kanal === 'print' && h.enAz >= 10 && (
                  <span className="alt" style={{ margin: 0 }}>
                    {' '}
                    Kâğıt set bu haftada {h.enAz} sayfa civarında olacak.
                  </span>
                )}
              </div>
            ))}
            <p className="alt" style={{ margin: '6px 0 0' }}>
              Sayı yaklaşık: egzersizlerin süresi farklı ve seçim program
              oluşturulduğunda kesinleşiyor. Çıkan setten istemediğinizi dosyadan
              kaldırabilir, ya da hiç program açmadan “Ödev ata” ile egzersizleri
              kendiniz seçebilirsiniz.
            </p>
          </div>
        )}
        <div className="dugmeler">
          <button className="birincil" disabled={bekliyor} onClick={() => void olustur()}>
            {bekliyor ? 'Oluşturuluyor…' : `${hafta} haftalık programı oluştur`}
          </button>
          <button onClick={onIptal} disabled={bekliyor}>
            Vazgeç
          </button>
        </div>
      </div>
    </>
  )
}
